// src/pages/Invoices.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Loader2,
    FileText,
    Sparkles,
    ChevronLeft,
    ChevronRight,
    CreditCard,
    Wallet,
} from 'lucide-react';
import { clientInvoicesAPI, clientPaymentsAPI, walletAPI, toSafeNumber } from '@/services/api';
import { toast } from 'sonner';

const fa = new Intl.NumberFormat('fa-IR');

type Invoice = {
    id: number;
    product_id: number;
    product_title?: string | null;
    amount: string | number;
    status: string;
    created_at: string;
    order_id: number;
    order_status: string;
    quantity: number;
    total_price: string;
    buyer_account_id?: number;
    buyer_first_name?: string;
    buyer_last_name?: string;
    buyer_phone_number?: string;
    buyer_email?: string;
    reference_code?: number;
    order_ref?: number;
};

const statusBadge = (status: string) => {
    switch (status) {
        case 'paid': return { className: 'bg-emerald-600 text-white', label: 'پرداخت‌شده' };
        case 'pending': return { className: 'bg-amber-500 text-white', label: 'در انتظار' };
        case 'unpaid': return { className: 'bg-amber-500 text-white', label: 'پرداخت‌نشده' };
        case 'cancelled': return { className: 'bg-red-600 text-white', label: 'لغو شده' };
        default: return { className: 'bg-gray-200 text-gray-700', label: status };
    }
};

const Invoices: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const totalPages = Math.max(1, Math.ceil(total / limit));

    const [items, setItems] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    const [detailLoading, setDetailLoading] = useState(false);
    const [detailErr, setDetailErr] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

    const [payingInvoiceId, setPayingInvoiceId] = useState<number | null>(null);
    const [walletBalance, setWalletBalance] = useState<number>(0);

    const loadWalletBalance = useCallback(async () => {
        try {
            const wal = await walletAPI.getWalletSummary();
            setWalletBalance(Number(wal.balance || 0));
        } catch {
            // ignore
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setErr('');
        try {
            const res = await clientInvoicesAPI.list({ page, limit });
            if (!res?.success) throw new Error('خطا در دریافت فاکتورها');
            setItems(res.items || []);
            setTotal(res.total || 0);
            await loadWalletBalance();
        } catch (e: any) {
            setErr(e?.message || 'خطا در دریافت اطلاعات');
        } finally {
            setLoading(false);
        }
    }, [page, limit, loadWalletBalance]);

    const loadDetail = useCallback(async (invoiceId: number) => {
        const safeId = toSafeNumber(invoiceId, 0);
        if (safeId <= 0) {
            setDetailErr('شناسه فاکتور نامعتبر است.');
            setDetailLoading(false);
            return;
        }

        setDetailLoading(true);
        setDetailErr('');
        setSelectedInvoice(null);
        try {
            const res = await clientInvoicesAPI.getById(safeId);
            if (!res?.success || !res.invoice) {
                throw new Error('فاکتور یافت نشد یا متعلق به شما نیست.');
            }
            setSelectedInvoice(res.invoice as Invoice);
        } catch (e: any) {
            setDetailErr(e?.message || 'خطا در دریافت جزئیات فاکتور');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // ✅ اصلاح اصلی: تابع پرداخت با دریافت صحیح payload
    const handlePayInvoice = useCallback(async (invoiceId: number) => {
        const safeId = toSafeNumber(invoiceId, 0);
        if (safeId <= 0) {
            toast.error('شناسه فاکتور نامعتبر است.');
            return;
        }

        setPayingInvoiceId(safeId);
        try {
            // ✅ ارسال payload به صورت آبجکت با کلید invoice_id
            const result = await clientPaymentsAPI.create({ invoice_id: safeId });
            if (result?.payment_url) {
                window.location.href = result.payment_url;
            } else {
                toast.success('فاکتور با موفقیت پرداخت شد.');
                await loadData();
                if (selectedInvoice && selectedInvoice.id === safeId) {
                    await loadDetail(safeId);
                }
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'خطا در پرداخت فاکتور';
            toast.error(msg);
        } finally {
            setPayingInvoiceId(null);
        }
    }, [loadData, loadDetail, selectedInvoice]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (id) {
            const numericId = toSafeNumber(id, 0);
            if (numericId > 0) {
                loadDetail(numericId);
            } else {
                setDetailErr('شناسه فاکتور نامعتبر است.');
                setSelectedInvoice(null);
            }
        } else {
            setSelectedInvoice(null);
            setDetailErr('');
        }
    }, [id, loadDetail]);

    const stats = useMemo(() => {
        const paid = items.filter(i => i.status === 'paid').length;
        const pending = items.filter(i => i.status === 'pending' || i.status === 'unpaid').length;
        const totalAmountRial = items.reduce((sum, inv) => {
            const v = typeof inv.amount === 'string' ? parseFloat(inv.amount) || 0 : inv.amount;
            return sum + v;
        }, 0);
        return { paid, pending, totalAmountRial };
    }, [items]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50">
                <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
            </div>
        );
    }

    // ---- Detail View ----
    if (id) {
        if (detailLoading) {
            return (
                <div className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50">
                    <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
                </div>
            );
        }

        if (detailErr || !selectedInvoice) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50" dir="rtl">
                    <Alert variant="destructive" className="max-w-md w-full mb-4">
                        <AlertDescription>{detailErr || 'فاکتور یافت نشد.'}</AlertDescription>
                    </Alert>
                    <Button variant="outline" onClick={() => navigate('/invoices')} className="rounded-xl">
                        بازگشت به لیست فاکتورها
                    </Button>
                </div>
            );
        }

        const inv = selectedInvoice;
        const amountRial = typeof inv.amount === 'string' ? parseFloat(inv.amount) || 0 : inv.amount;
        const { className, label } = statusBadge(inv.status);
        const isPending = inv.status === 'pending' || inv.status === 'unpaid';

        return (
            <div dir="rtl" className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50 flex flex-col gap-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 shadow-2xl">
                    <div className="relative flex flex-col gap-3 text-white">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-7 w-7" />
                            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                                <FileText className="h-7 w-7" />
                                فاکتور {inv.reference_code ? `#${inv.reference_code}` : `#${inv.id}`}
                            </h1>
                        </div>
                        <p className="text-emerald-100 text-sm md:text-base">جزئیات کامل فاکتور</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                            <span className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1 ${className}`}>
                                {label}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs bg-white/15 border border-white/40">
                                مبلغ: {fa.format(amountRial / 10)} تومان
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs bg-white/15 border border-white/40">
                                <Wallet className="h-3 w-3 inline ml-1" />
                                موجودی: {fa.format(walletBalance / 10)} تومان
                            </span>
                        </div>
                    </div>
                </div>

                <div className="max-w-2xl mx-auto w-full">
                    <Card className="rounded-3xl shadow-xl bg-white/90 backdrop-blur-sm border-emerald-100">
                        <CardHeader>
                            <CardTitle className="text-emerald-900 flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                جزئیات فاکتور
                            </CardTitle>
                            <CardDescription className="text-emerald-700">
                                اطلاعات محصول، خریدار و وضعیت
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm text-gray-800">
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">شماره فاکتور:</span>
                                <span className="font-mono">{inv.reference_code || inv.id}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">محصول:</span>
                                <span className="font-medium">{inv.product_title || `محصول #${inv.product_id}`}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">شناسه سفارش:</span>
                                <span className="font-mono">{inv.order_ref || `#${inv.order_id}`}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">تعداد:</span>
                                <span>{fa.format(inv.quantity)}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">مبلغ:</span>
                                <span className="font-semibold text-emerald-700">{fa.format(amountRial / 10)} تومان</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">تاریخ ثبت:</span>
                                <span>{new Date(inv.created_at).toLocaleString('fa-IR')}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500">وضعیت سفارش:</span>
                                <Badge className={inv.order_status === 'paid' ? 'bg-emerald-600' : 'bg-amber-500'}>
                                    {inv.order_status === 'paid' ? 'پرداخت‌شده' : 'در انتظار'}
                                </Badge>
                            </div>

                            {inv.buyer_first_name && (
                                <>
                                    <div className="pt-2 font-semibold text-emerald-800">اطلاعات خریدار</div>
                                    <div className="flex justify-between border-b pb-2">
                                        <span className="text-gray-500">نام:</span>
                                        <span>{inv.buyer_first_name} {inv.buyer_last_name}</span>
                                    </div>
                                    <div className="flex justify-between border-b pb-2">
                                        <span className="text-gray-500">موبایل:</span>
                                        <span dir="ltr">{inv.buyer_phone_number}</span>
                                    </div>
                                    {inv.buyer_email && (
                                        <div className="flex justify-between border-b pb-2">
                                            <span className="text-gray-500">ایمیل:</span>
                                            <span>{inv.buyer_email}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="flex justify-between">
                                <span className="text-gray-500">وضعیت فاکتور:</span>
                                <span className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1 ${className}`}>
                                    {label}
                                </span>
                            </div>

                            {isPending && (
                                <div className="pt-4 border-t border-amber-200">
                                    <Button
                                        onClick={() => handlePayInvoice(inv.id)}
                                        disabled={payingInvoiceId === inv.id}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                                    >
                                        {payingInvoiceId === inv.id ? (
                                            <>
                                                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                                در حال پرداخت…
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="ml-2 h-4 w-4" />
                                                پرداخت فاکتور
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="mt-4 flex justify-between items-center">
                        <Button variant="outline" onClick={() => navigate('/invoices')} className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                            بازگشت به لیست فاکتورها
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ---- List View ----
    if (err) {
        return (
            <div className="p-6" dir="rtl">
                <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>
                <Button variant="outline" onClick={loadData} className="mt-3">تلاش مجدد</Button>
            </div>
        );
    }

    return (
        <div dir="rtl" className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50">
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 md:p-8 shadow-2xl">
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-2xl md:text-4xl font-bold">فاکتورها</h1>
                        </div>
                        <p className="text-emerald-100">لیست فاکتورهای مربوط به سفارش‌های شما</p>
                        <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs bg-white/20 px-3 py-1 rounded-full">
                                موجودی کیف پول: {fa.format(walletBalance / 10)} تومان
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white border border-white/30">
                            <div className="text-xs opacity-90">کل</div>
                            <div className="text-2xl font-bold">{fa.format(total)}</div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white border border-white/30">
                            <div className="text-xs opacity-90">پرداخت‌شده</div>
                            <div className="text-2xl font-bold text-emerald-200">{fa.format(stats.paid)}</div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white border border-white/30">
                            <div className="text-xs opacity-90">در انتظار</div>
                            <div className="text-2xl font-bold text-amber-200">{fa.format(stats.pending)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* List Card */}
            <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                <CardHeader className="border-b border-emerald-100 pb-3 bg-gradient-to-r from-emerald-50 to-emerald-50/70 rounded-t-2xl">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <CardTitle className="text-emerald-900">لیست فاکتورها</CardTitle>
                            <CardDescription className="text-emerald-700">مبلغ، وضعیت و تاریخ فاکتورها</CardDescription>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-emerald-700">
                            <span className="hidden sm:inline">تعداد در صفحه:</span>
                            <select
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value))}
                                className="rounded-xl border border-emerald-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                                <option value={5}>۵</option>
                                <option value={10}>۱۰</option>
                                <option value={20}>۲۰</option>
                                <option value={50}>۵۰</option>
                            </select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-4">
                    {items.length === 0 ? (
                        <div className="py-12 text-center text-gray-600">
                            <FileText className="h-12 w-12 mx-auto mb-4 text-emerald-200" />
                            <div className="text-lg font-semibold text-gray-900">فاکتوری ثبت نشده است</div>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table */}
                            <div className="hidden md:block overflow-x-auto rounded-2xl border border-emerald-100">
                                <table className="w-full text-right text-sm">
                                    <thead className="bg-emerald-50 text-emerald-800">
                                    <tr>
                                        <th className="py-3 px-4 font-semibold">شماره</th>
                                        <th className="py-3 px-4 font-semibold">محصول</th>
                                        <th className="py-3 px-4 font-semibold">مبلغ</th>
                                        <th className="py-3 px-4 font-semibold">وضعیت</th>
                                        <th className="py-3 px-4 font-semibold">تاریخ</th>
                                        <th className="py-3 px-4 font-semibold">عملیات</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {items.map((inv) => {
                                        const { className, label } = statusBadge(inv.status);
                                        const amountRial = typeof inv.amount === 'string' ? parseFloat(inv.amount) || 0 : inv.amount;
                                        const isPending = inv.status === 'pending' || inv.status === 'unpaid';
                                        const invoiceId = Number(inv.id);
                                        if (isNaN(invoiceId) || invoiceId <= 0) return null;

                                        return (
                                            <tr key={inv.id} className="border-b last:border-0 hover:bg-emerald-50/60 transition">
                                                <td className="py-2.5 px-4 font-mono text-gray-800">
                                                    {inv.reference_code ? `#${inv.reference_code}` : `#${inv.id}`}
                                                </td>
                                                <td className="py-2.5 px-4">
                                                    {inv.product_title || `محصول #${inv.product_id}`}
                                                    <div className="text-[11px] text-gray-500">سفارش #{inv.order_ref || inv.order_id}</div>
                                                </td>
                                                <td className="py-2.5 px-4 font-bold text-emerald-700">{fa.format(amountRial / 10)} تومان</td>
                                                <td className="py-2.5 px-4">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${className}`}>
                                                        {label}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-4 text-gray-700">{new Date(inv.created_at).toLocaleString('fa-IR')}</td>
                                                <td className="py-2.5 px-4">
                                                    <div className="flex flex-wrap gap-2">
                                                        <Link to={`/invoices/${inv.id}`}>
                                                            <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl">
                                                                جزئیات
                                                            </Button>
                                                        </Link>
                                                        {isPending && (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handlePayInvoice(invoiceId)}
                                                                disabled={payingInvoiceId === invoiceId}
                                                                className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white"
                                                            >
                                                                {payingInvoiceId === invoiceId ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <CreditCard className="h-3.5 w-3.5 ml-1" />
                                                                )}
                                                                پرداخت
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="md:hidden space-y-3">
                                {items.map((inv) => {
                                    const { className, label } = statusBadge(inv.status);
                                    const amountRial = typeof inv.amount === 'string' ? parseFloat(inv.amount) || 0 : inv.amount;
                                    const isPending = inv.status === 'pending' || inv.status === 'unpaid';
                                    const invoiceId = Number(inv.id);
                                    if (isNaN(invoiceId) || invoiceId <= 0) return null;

                                    return (
                                        <div key={inv.id} className="rounded-2xl border border-emerald-500 bg-white shadow-sm p-3 flex flex-col gap-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-8 w-8 rounded-xl bg-emerald-50 flex items-center justify-center">
                                                        <FileText className="h-4 w-4 text-emerald-700" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-gray-500">
                                                            فاکتور {inv.reference_code ? `#${inv.reference_code}` : `#${inv.id}`}
                                                        </span>
                                                        <span className="text-sm font-semibold text-gray-900">{inv.product_title || `محصول #${inv.product_id}`}</span>
                                                    </div>
                                                </div>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] inline-flex items-center gap-1 ${className}`}>
                                                    {label}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-[11px] text-gray-600">
                                                <span>مبلغ:</span>
                                                <span className="font-semibold text-emerald-700">{fa.format(amountRial / 10)} تومان</span>
                                            </div>
                                            <div className="flex justify-between text-[11px] text-gray-500">
                                                <span>تاریخ:</span>
                                                <span>{new Date(inv.created_at).toLocaleString('fa-IR')}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <Link to={`/invoices/${inv.id}`} className="flex-1">
                                                    <Button variant="outline" size="sm" className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl">
                                                        جزئیات
                                                    </Button>
                                                </Link>
                                                {isPending && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handlePayInvoice(invoiceId)}
                                                        disabled={payingInvoiceId === invoiceId}
                                                        className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white"
                                                    >
                                                        {payingInvoiceId === invoiceId ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            'پرداخت'
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-4 border-t border-emerald-100">
                                    <div className="text-sm text-gray-600 order-2 sm:order-1">
                                        نمایش {fa.format(items.length)} از {fa.format(total)} | صفحه {fa.format(page)} از {fa.format(totalPages)}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1 order-1 sm:order-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                                            disabled={page === 1}
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                            قبلی
                                        </Button>
                                        <span className="px-3 py-1 text-sm text-gray-600">
                                            {fa.format(page)} / {fa.format(totalPages)}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                                            disabled={page === totalPages}
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        >
                                            بعدی
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Invoices;
